import { apiClient } from '@lib/httpClient/apiClient';
import { addTreatmentToFavoriteEndpoint } from '@lib/httpClient/endpoints/treatments/addTreatmentToFavoriteEndpoint';
import {
  getTreatmentDetailsEndpoint,
  type IGetTreatmentDetailsEndpointResponse,
} from '@lib/httpClient/endpoints/treatments/getTreatmentDetailsEndpoint';
import {
  type IListTreatmentCategoriesEndpointResponse,
  listTreatmentCategoriesEndpoint,
} from '@lib/httpClient/endpoints/treatments/listTreatmentCategoriesEndpoint';
import {
  type IListTreatmentsEndpointQuery,
  type IListTreatmentsEndpointResponse,
  listTreatmentsEndpoint,
} from '@lib/httpClient/endpoints/treatments/listTreatmentsEndpoint';
import { removeTreatmentToFavoriteEndpoint } from '@lib/httpClient/endpoints/treatments/removeTreatmentToFavoriteEndpoint';

import type { IAddTreatmentToFavoriteInputDTO } from './dtos/addToFavorites/IInputData';
import type { IGetTreatmentDetailsInputDTO } from './dtos/getDetails/IInputData';
import type { IGetTreatmentDetailsOutputDTO } from './dtos/getDetails/IOutputData';
import type { IListTreatmentsInputDTO } from './dtos/list/IInputData';
import type { IListTreatmentsOutputDTO } from './dtos/list/IOutputData';
import type { IListTreatmentCategoriesOutputDTO } from './dtos/listCategories/IOutputData';
import type { IRemoveTreatmentToFavoriteInputDTO } from './dtos/removeToFavorites/IInputData';
import type { ITreatmentRepository } from './ITreatmentRepository';

class TreatmentService implements ITreatmentRepository {
  public async addToFavorites(
    input: IAddTreatmentToFavoriteInputDTO,
  ): Promise<void> {
    await apiClient.post({
      path: addTreatmentToFavoriteEndpoint(input.id),
    });
  }

  public async getDetails(
    input: IGetTreatmentDetailsInputDTO,
  ): Promise<IGetTreatmentDetailsOutputDTO> {
    const response = await apiClient.get<IGetTreatmentDetailsEndpointResponse>({
      path: getTreatmentDetailsEndpoint(input.id),
    });
    const output: IGetTreatmentDetailsOutputDTO = {
      info: {
        id: response.id,
        isAvailable: response.is_available_website,
        isFavorite: response.is_favorite,
        title: response.title,
        topics: response.topics.map((topic) => ({
          content: topic.content,
          id: topic.id,
          title: topic.title,
        })),
      },
    };
    return output;
  }

  public async list(
    input: IListTreatmentsInputDTO,
  ): Promise<IListTreatmentsOutputDTO> {
    const response = await apiClient.get<
      IListTreatmentsEndpointResponse,
      IListTreatmentsEndpointQuery
    >({
      path: listTreatmentsEndpoint(),
      params: {
        medical_specialty_id: input.medicalSpecialtyId,
        sort: input.sort,
        treatment_category_id: input.treatmentCategoryId,
      },
    });
    const output: IListTreatmentsOutputDTO = {
      list: response.list.map((item) => ({
        id: item.id,
        isAvailable: item.is_available_website,
        isFavorite: item.is_favorite,
        title: item.title,
      })),
    };
    return output;
  }

  public async listCategories(): Promise<IListTreatmentCategoriesOutputDTO> {
    const response =
      await apiClient.get<IListTreatmentCategoriesEndpointResponse>({
        path: listTreatmentCategoriesEndpoint(),
      });
    const output: IListTreatmentCategoriesOutputDTO = {
      list: response.list.map((item) => ({
        id: item.id,
        title: item.title,
      })),
    };
    return output;
  }

  public async removeToFavorites(
    input: IRemoveTreatmentToFavoriteInputDTO,
  ): Promise<void> {
    await apiClient.delete({
      path: removeTreatmentToFavoriteEndpoint(input.id),
    });
  }
}

export const treatmentService = new TreatmentService();
